import 'client/assets/vanakit/themes/flatwhite.css'
import 'client/pages/app/styles/tailwind.css'
import 'client/styles/global.css'
import 'client/styles/vanakit-override.less'
import 'client/pages/app/styles/index.less'
import 'utils/logger'
import { $T, getCurrentLanguage, initTranslation, type Lang, LANG_LABELS, setCurrentLanguage } from 'client/library/translation'
import { mount } from 'utils/vue-utils'
import AppComponent from './app.vue'
import EmbedEditorComponent from './patterns/embed-editor.vue'
import { isUnderElementWithClass } from 'utils/dom'
import { loadVanakit } from 'utils/load-vanakit'
import { setupWorkbookGlobalConfig } from 'client/library/workbook-next/workbook-global-config'
import { changeAppearance } from 'utils/appearance'
import { setEmbedManifest } from 'client/manifests'
import { createRouter, createWebHistory } from 'vue-router'
import globalComponents from 'client/components/global-components'
import { useVueRouter } from 'client/composables/vue-router'
import { Storage } from 'app/services/storage'
import { useServiceStore } from 'client/store'
import { useContextStore } from 'client/store/context-store'
import { MapFocusPlugin } from 'app/services/plugins/map-focus-plugin'
import { Action } from './action/actions'
import { IS_TEST } from 'client/constants'

const isDev = process.env.NODE_ENV === 'development' || IS_TEST

const init = async () => {
  setupWorkbookGlobalConfig({
    guidedPayment: async () => undefined,
    featureIsCurrentlyRestricted: () => false,
    snowmonsterGlobalConfig: {
      minimumZoomScale: 0.01,
      builtinImageUrls: undefined,
    },
  })

  const router = createRouter({
    history: createWebHistory(),
    routes: [
      {
        name: 'root',
        path: '/',
        redirect: 'xmind-embed-editor',
        beforeEnter: async to => {
          const lang = to.query['lang'] as Lang
          if (lang) await setCurrentLanguage(lang)
        },
        children: [
          {
            name: 'xmind-embed-editor',
            path: 'xmind-embed-editor/:version(\\d+)?',
            children: [
              { name: 'embed-editor-root', path: '', component: EmbedEditorComponent },
              { name: 'embed-editor-maker', path: 'maker', component: EmbedEditorComponent },
              isDev ? { name: 'dev', path: 'dev', component: () => import('./patterns/dev-iframe.vue') } : null,
            ].filter(Boolean),
          },
        ].filter(Boolean),
      },
    ],
  })

  router.beforeEach(async (to, _, next) => {
    const lang = (to.query.lang as Lang) || 'en-US'
    if (LANG_LABELS[lang] && lang !== getCurrentLanguage()) {
      await setCurrentLanguage(lang)
    }
    next()
  })

  await initTranslation()

  await loadVanakit()
  mount(AppComponent, {
    el: document.querySelector('#app') as HTMLDivElement,
    plugins: [router, globalComponents],
    provides: {
      $T,
    },
  })

  const storage = new Storage()
  const action = new Action()

  useServiceStore().addService('storage', storage)
  useServiceStore().addService('action', action)
  useVueRouter().setRouter(router)
  useContextStore().setContext('isEmbedEditorRuntime', true)

  MapFocusPlugin()
  preventContextMenuOnImage()
  preventDocumentContextMenu()
}

setEmbedManifest()
changeAppearance('default')
init()

function preventContextMenuOnImage() {
  document.addEventListener('contextmenu', e => {
    if (!(e.target instanceof Element)) return
    if (isUnderElementWithClass(e.target, 'sheet-viewer')) return
    if (e.target instanceof HTMLImageElement || e.target.tagName === 'SVG' || e.target.closest('svg')) {
      e.preventDefault()
    }
  })
}

function preventDocumentContextMenu() {
  if (!document) {
    logger.warn('window.document not found')
    return
  }
  document.addEventListener('contextmenu', event => {
    event.preventDefault()
  })
}
